import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Search, User, FolderOpen } from 'lucide-react';
import SendMessageForm from './SendMessageForm';
import useModal from '../../hooks/useModal';

const NewMessageModal = ({ onClose, onMessageSent, preSelectedClientId, preSelectedCaseId }) => {
  const [step, setStep] = useState(preSelectedClientId ? 'compose' : 'select');
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(!preSelectedClientId);
  const [preSelectLoading, setPreSelectLoading] = useState(!!preSelectedClientId);
  const searchTimeoutRef = useRef(null);

  // Modal behavior
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(true, onClose);

  // When preSelectedClientId is provided, fetch that client directly by ID
  useEffect(() => {
    if (!preSelectedClientId) return;
    let cancelled = false;
    const fetchClient = async () => {
      try {
        setPreSelectLoading(true);
        const api = await import('../../services/api');
        const response = await api.default.get(`/clients/${preSelectedClientId}`);
        if (cancelled) return;
        const clientData = response.data;
        if (clientData) {
          setSelectedClient(clientData);
        }
      } catch (error) {
        console.error('Error loading pre-selected client:', error);
        // Fall back to client selection step
        if (!cancelled) {
          setStep('select');
        }
      } finally {
        if (!cancelled) {
          setPreSelectLoading(false);
        }
      }
    };
    fetchClient();
    return () => { cancelled = true; };
  }, [preSelectedClientId]);

  // When preSelectedCaseId is provided, fetch that case to display its info
  useEffect(() => {
    if (!preSelectedCaseId) return;
    let cancelled = false;
    const fetchCase = async () => {
      try {
        const api = await import('../../services/api');
        const response = await api.default.get(`/cases/${preSelectedCaseId}`);
        if (cancelled) return;
        if (response.data) {
          setSelectedCase(response.data);
        }
      } catch (error) {
        console.error('Error loading pre-selected case:', error);
        // Case info is not critical - communication will still be linked by ID
      }
    };
    fetchCase();
    return () => { cancelled = true; };
  }, [preSelectedCaseId]);

  // Search clients from server with debounce
  const searchClients = useCallback(async (query) => {
    try {
      setLoading(true);
      const api = await import('../../services/api');
      const params = { limit: 50 };
      if (query && query.trim()) {
        params.search = query.trim();
      }
      const response = await api.default.get('/clients', { params });
      const clientsData = Array.isArray(response.data?.data) ? response.data.data :
                          Array.isArray(response.data) ? response.data : [];
      setClients(clientsData);
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load initial clients when on select step (no preSelectedClientId)
  useEffect(() => {
    if (!preSelectedClientId) {
      searchClients('');
    }
  }, [preSelectedClientId, searchClients]);

  // Debounced search when search term changes
  useEffect(() => {
    if (preSelectedClientId && step === 'compose') return;

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchClients(searchTerm);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, searchClients, preSelectedClientId, step]);

  const loadClientCases = useCallback(async () => {
    try {
      const api = await import('../../services/api');
      const response = await api.default.get(`/cases/client/${selectedClient.id}`);
      const casesData = Array.isArray(response.data) ? response.data : [];
      setCases(casesData);
    } catch (error) {
      console.error('Error loading cases:', error);
      setCases([]);
    }
  }, [selectedClient]);

  useEffect(() => {
    if (selectedClient) {
      loadClientCases();
    }
  }, [selectedClient, loadClientCases]);

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setSelectedCase(null);
    setStep('compose');
  };

  const handleCaseSelect = (caseItem) => {
    setSelectedCase(caseItem);
  };

  const handleContinue = () => {
    if (selectedClient) {
      setStep('compose');
    }
  };

  const handleSendMessage = async (messageData) => {
    try {
      const communicationService = await import('../../services/communicationService');

      if (messageData.type === 'sms') {
        await communicationService.default.sendSMS(messageData);
      } else if (messageData.type === 'email') {
        await communicationService.default.sendEmail(messageData);
      }

      onMessageSent();
      onClose();
    } catch (error) {
      console.error('NewMessageModal: Error sending message:', error);
      throw error;
    }
  };

  const handleChangeClient = () => {
    setStep('select');
    // Load the client list if we came from a pre-selected flow
    if (clients.length === 0) {
      searchClients('');
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 z-[9999]"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10"></div>
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">
            {step === 'select' ? 'Select Client' : `Send Message to ${selectedClient?.first_name} ${selectedClient?.last_name}`}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {preSelectLoading ? (
          <div className="p-6 text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading client...</p>
          </div>
        ) : step === 'select' ? (
          <div className="p-6">
            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  autoFocus
                />
              </div>
            </div>

            {/* Client List */}
            <div className="space-y-2 mb-6">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Loading clients...</p>
                </div>
              ) : clients.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <User className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>No clients found</p>
                </div>
              ) : (
                clients.map(client => (
                  <div
                    key={client.id}
                    onClick={() => handleClientSelect(client)}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedClient?.id === client.id
                        ? 'border-yellow-500 bg-yellow-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {client.first_name} {client.last_name}
                        </h3>
                        {client.company_name && (
                          <p className="text-sm text-gray-600">{client.company_name}</p>
                        )}
                        <div className="flex gap-4 mt-1 text-sm text-gray-500">
                          {client.email && <span>{client.email}</span>}
                          {client.phone && <span>{client.phone}</span>}
                        </div>
                      </div>
                      <User className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Case Selection (if client selected) */}
            {selectedClient && cases.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Select Case (Optional)
                </h3>
                <div className="space-y-2">
                  <div
                    onClick={() => setSelectedCase(null)}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      !selectedCase
                        ? 'border-yellow-500 bg-yellow-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-sm text-gray-700">No specific case</span>
                  </div>

                  {cases.map(caseItem => (
                    <div
                      key={caseItem.id}
                      onClick={() => handleCaseSelect(caseItem)}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedCase?.id === caseItem.id
                          ? 'border-yellow-500 bg-yellow-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <FolderOpen className="w-4 h-4 text-gray-500" />
                        <div>
                          <span className="text-sm font-medium">{caseItem.title}</span>
                          <span className="text-sm text-gray-500 ml-2">
                            ({caseItem.case_number})
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleContinue}
                disabled={!selectedClient}
                className="flex-1 bg-yellow-500 text-white py-2 px-4 rounded-lg hover:bg-yellow-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Continue
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            {/* Selected Client/Case Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">
                    {selectedClient.first_name} {selectedClient.last_name}
                  </h3>
                  {selectedCase && (
                    <p className="text-sm text-gray-600">
                      Case: {selectedCase.title} ({selectedCase.case_number})
                    </p>
                  )}
                </div>
                <button
                  onClick={handleChangeClient}
                  className="text-yellow-600 hover:text-yellow-600 text-sm"
                >
                  Change
                </button>
              </div>
            </div>

            {/* Message Form */}
            <SendMessageForm
              clientId={selectedClient.id}
              caseId={selectedCase?.id || preSelectedCaseId}
              clientName={`${selectedClient.first_name} ${selectedClient.last_name}`}
              onSend={handleSendMessage}
              onCancel={handleChangeClient}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NewMessageModal;
